// DirectAdjustCard — agent SPA variant.
//
// Replaces the legacy ManualRechargeForm + ManualSubAgentRechargeForm
// with the same compact "tap-to-browse, pick, modal" pattern the
// admin SPA uses. Tighter footprint than the legacy forms, plus the
// browse-all-on-focus behaviour so agents who forget a name can
// scroll through their roster without typing.
//
// ─── Three business rules baked into this card ────────────────────
//
//   1. SCOPE: the calling agent can only see / pick their OWN
//      players (via /admin/users which auto-scopes to req.admin.id
//      for role='agent') or their OWN direct sub-agents
//      (via /admin/me-agent/sub-agents which is already scoped to
//      WHERE parent_agent_id = req.admin.id). No way to credit a
//      stranger or someone in a sibling agent's downline.
//
//   2. CREDIT ONLY: this card is mounted ONLY in the Recharge tab
//      of each hub. There is no withdrawal-direction variant —
//      withdrawals from a player or sub-agent must come through the
//      request-queue flow (the agent approves or marks a paid
//      withdrawal request submitted BY the player or sub-agent).
//      This matches a long-standing business rule: agents can SEND
//      money outward but cannot pull money inward without consent.
//
//   3. OWN POOL ONLY: the modal's funding source is fixed to the
//      calling agent's wallet. The agent can never credit a player
//      "from the platform" or from another agent's pool. The server
//      enforces this too — both endpoints debit the calling agent's
//      wallet atomically and throw INSUFFICIENT_FUNDS if the move
//      would overdraw.
//
// The card's scope is fixed by the `scope` prop. The Player Requests
// hub passes 'players', the Agent Requests hub passes 'subagents'.

import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useDownlineLabel } from '@baaligames/shared-ui/admin/lib/downlineLabel';
import DirectWalletAdjustModal, {
  type AgentDirectAdjustSubject,
} from './DirectWalletAdjustModal';

type Scope = 'players' | 'subagents';

type PlayerHit = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_image_url: string | null;
  status: string;
  wallet_balance: number;
};

type SubagentHit = {
  id: number;
  uaid: string | null;
  username: string;
  email: string;
  avatar_url: string | null;
  status: string;
  level: number;
  wallet_balance: number;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function AgentDirectAdjustCard({
  scope, onAfterAdjust, preselectId,
}: {
  scope: Scope;
  onAfterAdjust: () => void;
  // When a player/sub-agent detail page deep-links here with
  // ?preselect=<id>, we resolve that exact subject and pop the recharge
  // modal open immediately — the broker never has to search + pick.
  // This is the whole point of the "Recharge" CTA on a detail page:
  // land ready-to-type, not on a search box (2026-08-06 UX fix).
  preselectId?: number;
}) {
  const [query, setQuery]         = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults]     = useState<Array<PlayerHit | SubagentHit>>([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState<string | null>(null);
  const [open, setOpen]           = useState(false);
  const [picked, setPicked]       = useState<AgentDirectAdjustSubject | null>(null);
  const [preselectDone, setPreselectDone] = useState(false);
  const t = useT();
  const dl = useDownlineLabel();   // downline role (Master/Agent) for the subagents scope

  // Scope-driven labels + endpoints. Players → /admin/users
  // (auto-scoped on role=agent to the agent's own roster).
  // Subagents → /admin/me-agent/sub-agents (already scoped to
  // direct children only). The subagent copy names the actual role.
  const placeholder = scope === 'players'
    ? t('agent.directRecharge.search.player')
    : `Search a ${dl.lower} to recharge (name · email · UAID)…`;
  const subtitle = scope === 'players'
    ? t('agent.directRecharge.subtitle.player')
    : `Pick a ${dl.lower} and recharge their pool from yours.`;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const search = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      if (scope === 'players') {
        // /admin/users supports both `search` and `limit`. With agent
        // role the controller's ownershipScope hard-locks the result
        // set to the agent's own players regardless of what the
        // client sends — see adminUsers.controller.js#list.
        const q = debounced ? `search=${encodeURIComponent(debounced)}&` : '';
        const r = await adminApi.get<{ rows: PlayerHit[] }>(`/admin/users?${q}limit=15`);
        setResults(r.rows);
      } else {
        // /admin/me-agent/sub-agents now accepts an optional `search`
        // param (added 2026-05-16 on meAgentSubAgents.controller.js#list).
        // The endpoint doesn't paginate — it returns every direct
        // child. Cap client-side to 15 to match the players list and
        // bound DOM size.
        const q = debounced ? `?search=${encodeURIComponent(debounced)}` : '';
        const r = await adminApi.get<{ rows: SubagentHit[] }>(`/admin/me-agent/sub-agents${q}`);
        setResults((r.rows || []).slice(0, 15));
      }
    } catch (e) { setErr((e as ApiError).message || t('agent.directRecharge.searchFailed')); }
    finally { setLoading(false); }
  }, [debounced, scope, t]);

  useEffect(() => { void search(); }, [search]);
  useEffect(() => { if (debounced.length > 0) setOpen(true); }, [debounced]);

  // ─── Preselect (deep-link) ────────────────────────────────────────
  // If the card mounts with a preselectId, fetch that one subject and
  // open the modal straight away. Runs once. If the fetch fails (e.g.
  // the id isn't in this agent's scope), we silently fall back to the
  // normal search UI so the card is never dead.
  useEffect(() => {
    if (!preselectId || preselectDone) return;
    let cancelled = false;
    (async () => {
      try {
        if (scope === 'players') {
          const { user: p } = await adminApi.get<{ user: PlayerHit }>(`/admin/users/${preselectId}`);
          if (cancelled || !p) return;
          const displayName = (p.first_name || p.last_name)
            ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
            : p.username;
          setPicked({
            kind: 'player', id: p.id, username: p.username, displayName,
            avatarUrl: p.profile_image_url, walletBalance: p.wallet_balance,
          });
        } else {
          const { agent: s } = await adminApi.get<{ agent: SubagentHit & { display_name?: string } }>(
            `/admin/me-agent/sub-agents/${preselectId}`,
          );
          if (cancelled || !s) return;
          setPicked({
            kind: 'subagent', id: s.id, username: s.username, avatarUrl: s.avatar_url,
            level: s.level, uaid: s.uaid, poolBalance: s.wallet_balance,
            displayName: s.display_name ?? null,
          });
        }
      } catch { /* out of scope / not found — fall back to manual search */ }
      finally { if (!cancelled) setPreselectDone(true); }
    })();
    return () => { cancelled = true; };
  }, [preselectId, preselectDone, scope]);

  function pick(hit: PlayerHit | SubagentHit) {
    if ('first_name' in hit) {
      const p = hit as PlayerHit;
      const displayName = (p.first_name || p.last_name)
        ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
        : p.username;
      setPicked({
        kind: 'player',
        id: p.id,
        username: p.username,
        displayName,
        avatarUrl: p.profile_image_url,
        walletBalance: p.wallet_balance,
      });
    } else {
      const s = hit as SubagentHit;
      setPicked({
        kind: 'subagent',
        id: s.id,
        username: s.username,
        avatarUrl: s.avatar_url,
        level: s.level,
        uaid: s.uaid,
        poolBalance: s.wallet_balance,
      });
    }
    setOpen(false);
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-white dark:bg-arena-700/40
                   border border-arena-200/55 dark:border-arena-600/40
                   shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                   overflow-hidden"
      >
        <div className="px-3 sm:px-4 py-3 flex items-center gap-2 border-b border-arena-200/40 dark:border-arena-700/40">
          <span className="grid place-items-center w-8 h-8 rounded-xl shrink-0
                           bg-emerald-50 text-emerald-700 border border-emerald-200/60
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/30">
            <Icon name="arrow_upward" size={16} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-bold text-neutral-900 dark:text-white leading-tight">
              {t('agent.directRecharge.title')}
            </div>
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 leading-tight mt-0.5">
              {subtitle}
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4">
          <div className="relative">
            <Icon name="search" size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/70 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              // Same idempotent-focus contract as the admin card: every
              // focus opens the dropdown AND fires search(), so the
              // browse list is always fresh and reappears after a
              // previous pick/submit cycle wiped results.
              onFocus={() => { setOpen(true); void search(); }}
              placeholder={placeholder}
              className="w-full rounded-full pl-9 pr-9 py-2 text-[13px]
                         bg-white dark:bg-arena-800/60
                         border border-arena-200/55 dark:border-arena-600/40
                         text-neutral-900 dark:text-white
                         placeholder-arena-400 dark:placeholder-white/40
                         outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400/40"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full
                           hover:bg-arena-100/60 dark:hover:bg-arena-700/70"
                aria-label={t('agent.directRecharge.clearSearch')}
              >
                <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/70" />
              </button>
            )}
          </div>

          {err && (
            <div className="mt-2 text-[11.5px] text-rose-700 dark:text-rose-300">{err}</div>
          )}

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="mt-2 rounded-xl border border-arena-200/55 dark:border-arena-600/40
                           bg-white dark:bg-arena-800/85 backdrop-blur
                           max-h-72 overflow-y-auto"
              >
                {loading && (
                  <div className="px-3 py-3 text-[12px] text-arena-600 dark:text-neon-200/70">
                    <Icon name="progress_activity" size={12} className="animate-spin inline align-text-bottom mr-1" />
                    {t('agent.directRecharge.searching')}
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="px-3 py-3 text-[12px] text-arena-600 dark:text-neon-200/55 italic">
                    {debounced
                      ? t('agent.directRecharge.noMatches')
                      : (scope === 'players' ? t('agent.directRecharge.empty.player') : t('agent.directRecharge.empty.subagent'))}
                  </div>
                )}
                {!loading && results.map((hit) => (
                  <ResultRow key={`${scope}:${(hit as any).id}`} scope={scope} hit={hit} onPick={() => pick(hit)} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {picked && (
        <DirectWalletAdjustModal
          subject={picked}
          onClose={() => setPicked(null)}
          onDone={() => {
            setPicked(null);
            setQuery('');
            setResults([]);
            onAfterAdjust();
          }}
        />
      )}
    </>
  );
}

function ResultRow({
  scope, hit, onPick,
}: {
  scope: Scope;
  hit: PlayerHit | SubagentHit;
  onPick: () => void;
}) {
  const t = useT();
  if (scope === 'players') {
    const p = hit as PlayerHit;
    const displayName = (p.first_name || p.last_name)
      ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
      : p.username;
    return (
      <button
        type="button"
        onClick={onPick}
        className="w-full px-3 py-2 flex items-center gap-2.5
                   hover:bg-arena-50 dark:hover:bg-arena-700/55 active:bg-arena-100/60 dark:active:bg-arena-700
                   text-left transition border-b border-arena-200/30 dark:border-arena-700/30 last:border-b-0"
      >
        <AdminAvatar url={p.profile_image_url} username={p.username} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate">
            {displayName}
          </div>
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
            {(((p.first_name || "") + " " + (p.last_name || "")).trim() || "—")} · #{p.id}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[12px] font-display font-extrabold text-sky-700 dark:text-sky-300 tabular-nums leading-none">
            {inr(p.wallet_balance)}
          </div>
          <div className="text-[9.5px] text-arena-500 dark:text-neon-200/55 leading-none mt-0.5">
            {t('agent.directRecharge.label.wallet')}
          </div>
        </div>
      </button>
    );
  }
  const s = hit as SubagentHit;
  return (
    <button
      type="button"
      onClick={onPick}
      className="w-full px-3 py-2 flex items-center gap-2.5
                 hover:bg-arena-50 dark:hover:bg-arena-700/55 active:bg-arena-100/60 dark:active:bg-arena-700
                 text-left transition border-b border-arena-200/30 dark:border-arena-700/30 last:border-b-0"
    >
      <AdminAvatar url={s.avatar_url} username={s.username} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate flex items-center gap-1.5">
          {(((s as { display_name?: string }).display_name) || "").trim() || "—"}
          {/* Level chip removed — agents must not see sub-agent levels (policy 2026-05-25). */}
        </div>
        <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
          #{s.id}
          {s.uaid && <span className="ml-1 font-mono">· {s.uaid}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[12px] font-display font-extrabold tabular-nums leading-none
          ${s.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          {inr(s.wallet_balance)}
        </div>
        <div className="text-[9.5px] text-arena-500 dark:text-neon-200/55 leading-none mt-0.5">
          {t('agent.directRecharge.label.pool')}
        </div>
      </div>
    </button>
  );
}
